/**
 * `<Toast />` — Story 5.4 (UX §4.1 line 887 + UX §4.3 line 906, AC line 1014).
 *
 * Non-blocking notification primitive. Three variants (success / error /
 * info) with automatic position detection (mobile bottom / desktop
 * top-right) via `window.matchMedia('(min-width: 640px)')`.
 *
 * **Critical Scope Decision #7**: position detection happens once at
 * mount (NOT on resize). The toast lifecycle is short (typically 5 sec)
 * so a resize during that window is rare; adding a resize listener
 * would be cargo-cult.
 *
 * **Critical Scope Decision #8**: NEVER mount Toast on broadcast
 * surfaces (UX §4.1 line 887 / UX §3.7). Story 5.4 does NOT enforce
 * this in the component — the gate is at the surface composition
 * level. Broadcast surfaces (Stories 12.x) simply never import Toast.
 *
 * No queue management:
 *   - Story 5.4 ships a render primitive only. Single-toast-at-a-time
 *     is the project default; consumers manage local toast state.
 *
 * Auto-dismiss:
 *   - `duration` defaults to 5000 ms; `0` disables auto-dismiss.
 *   - When the timer fires, the LATEST `onDismiss` reference is invoked
 *     via a ref (so unstable-identity arrow functions from the consumer
 *     don't reset the timer on every re-render).
 *   - If `onDismiss` is omitted, the toast simply remains rendered until
 *     the consumer unmounts it (the timer fires a no-op).
 *
 * Aria roles (UX §5.1 line 999):
 *   - `role="status"` for `success` / `info`.
 *   - `role="alert"` for `error`.
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

export type ToastVariant = 'success' | 'error' | 'info';
export type ToastPosition = 'bottom-mobile' | 'top-right-desktop';

export interface ToastProps {
  variant: ToastVariant;
  children: ReactNode;
  /** Defaults to mobile/desktop auto-detect via matchMedia. */
  position?: ToastPosition;
  /** Milliseconds; 0 disables auto-dismiss. Default 5000. */
  duration?: number;
  onDismiss?: () => void;
}

const DEFAULT_DURATION_MS = 5000;
const DESKTOP_BREAKPOINT = '(min-width: 640px)';

function detectPosition(): ToastPosition {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'bottom-mobile';
  }
  return window.matchMedia(DESKTOP_BREAKPOINT).matches
    ? 'top-right-desktop'
    : 'bottom-mobile';
}

function variantStyle(variant: ToastVariant): {
  background: string;
  color: string;
} {
  switch (variant) {
    case 'success':
      return { background: 'var(--success)', color: 'var(--text-on-brand)' };
    case 'error':
      return { background: 'var(--danger)', color: 'var(--text-on-brand)' };
    case 'info':
      return { background: 'var(--info)', color: 'var(--text-on-brand)' };
  }
}

function positionStyle(position: ToastPosition): React.CSSProperties {
  if (position === 'top-right-desktop') {
    return {
      position: 'fixed',
      top: 'var(--space-4)',
      right: 'var(--space-4)',
    };
  }
  return {
    position: 'fixed',
    bottom: 'var(--space-4)',
    left: '50%',
    transform: 'translateX(-50%)',
  };
}

/**
 * `Toast` — non-blocking notification.
 */
export function Toast({
  variant,
  children,
  position,
  duration = DEFAULT_DURATION_MS,
  onDismiss,
}: ToastProps) {
  const [resolvedPosition] = useState<ToastPosition>(
    () => position ?? detectPosition(),
  );

  // Hold the latest onDismiss in a ref so the auto-dismiss timer is set
  // up exactly once at mount and does NOT reset when the parent
  // re-renders with a new arrow-function `onDismiss`. This is the
  // standard pattern for "fire once after duration" with an
  // unstable-identity callback (avoids the timer-thrashing footgun
  // identified in code review).
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!duration || duration <= 0) return;
    const timer = window.setTimeout(() => {
      onDismissRef.current?.();
    }, duration);
    return () => window.clearTimeout(timer);
    // Mount-time-only: re-running on duration change would also reset
    // the timer, but `duration` is a primitive — stable across renders
    // for a given Toast instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  const v = variantStyle(variant);
  const role = variant === 'error' ? 'alert' : 'status';

  return (
    <div
      role={role}
      data-variant={variant}
      data-position={resolvedPosition}
      style={{
        background: v.background,
        color: v.color,
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elev-2)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm-size)',
        zIndex: 1000,
        maxWidth: 'min(90vw, 480px)',
        ...positionStyle(resolvedPosition),
      }}
    >
      {children}
    </div>
  );
}
